/* eslint-disable */

export const protobufPackage = "store.api.auth";

/** Token defines the common token response from service. */
export interface Token {
  /** Access token provides authority to access guarded API methods. */
  accessToken: string;
  /** Id token represents user information encoded in JWT format. */
  idToken: string;
  /** Refresh token for renew access token, optional */
  refreshToken: string;
  /** The type of the access token, always "Bearer". */
  tokenType: string;
  /** The number of seconds in which the access token expires. */
  expiresIn: number;
  /** Only for platform `ANDROID` and `IOS` */
  firebaseToken: string;
}

export interface UserRecord {
  /** A globally unique user id in system. */
  uid: string;
  /** A globally unique numeric id. */
  friendlyId: string;
  /** User primary email. */
  email: string;
  /** Indicates whether user primary email has been verified or not. */
  emailVerified: boolean;
  /** Display name of user. */
  displayName: string;
  /** Photo url of user. */
  photoUrl: string;
  /** User phone number in hashed value */
  phoneNumber:
    | HashData
    | undefined;
  /**
   * A set of id provider information.
   * A user is anonymous if there is no provider info.
   */
  providerUserInfo: ProviderUserInfo[];
  /** Disabled flag. Indicates whether user account is disabled or not. */
  disabled: boolean;
  /** 關閉下單 */
  prohibited: boolean;
}

export interface ProviderUserInfo {
  /** Provider id */
  providerId: string;
  /** A unique id from identity provider. */
  federatedId: string;
}

/** HashData defines a data holder for hashed values. */
export interface HashData {
  /** Hash value. */
  value: string;
  /** Method of hash. */
  method: string;
}

export interface OAuthCredential {
  /** A facebook app credential. Use `authorization_code` for grant type. */
  facebook?:
    | FacebookCredential
    | undefined;
  /** A facebook access token. Use `authorization_code` for grant type. */
  facebookToken?:
    | string
    | undefined;
  /** An apple app credential. Use `authorization_code` for grant type. */
  apple?:
    | AppleCredential
    | undefined;
  /** A legacy credential. Use `authorization_code` for grant type. */
  legacy?: LegacyCredential | undefined;
}

export interface VerifyCodeCredential {
  /** Given phone send sms verification code. */
  phone?:
    | string
    | undefined;
  /** Given phone call voice verification code. */
  phoneVoice?:
    | string
    | undefined;
  /** Specifies weather the resend timer should double. */
  resendLengthen: boolean;
}

/**
 * FacebookCredential defines OAuth credential model for facebook app.
 * Reference
 */
export interface FacebookCredential {
  /** Required. authorization code. */
  code: string;
  /** Required. redirect url. */
  redirectUrl: string;
  /** Optional. The nonce using when generating the authorization code, should leave empty if you don't use it. */
  nonce: string;
}

/**
 * AppleCredential defines OAuth credential model for apple app.
 * Reference
 */
export interface AppleCredential {
  /** Required. A code that received from ios app. */
  code: string;
  /** Optional. The nonce using when generating the authorization code, should leave empty if you don't use it. */
  nonce: string;
}

/**
 * LegacyCredential defines OAuth credential model for legacy system.
 * Reference
 */
export interface LegacyCredential {
  /** Required. device_id. */
  deviceId: string;
  /** Required. legacy access token. */
  token: string;
}

export interface PushSettings {
  /** Notify promotion flag. Indicates whether the account should receive promotion notifications. */
  notifyPromotion: boolean;
  /** Notify logistics flag. Indicates whether the account should receive logistics notifications. */
  notifyLogistics: boolean;
}

function createBaseToken(): Token {
  return { accessToken: "", idToken: "", refreshToken: "", tokenType: "", expiresIn: 0, firebaseToken: "" };
}

export const Token = {
  fromJSON(object: any): Token {
    return {
      accessToken: isSet(object.accessToken) ? String(object.accessToken) : "",
      idToken: isSet(object.idToken) ? String(object.idToken) : "",
      refreshToken: isSet(object.refreshToken) ? String(object.refreshToken) : "",
      tokenType: isSet(object.tokenType) ? String(object.tokenType) : "",
      expiresIn: isSet(object.expiresIn) ? Number(object.expiresIn) : 0,
      firebaseToken: isSet(object.firebaseToken) ? String(object.firebaseToken) : "",
    };
  },

  toJSON(message: Token): unknown {
    const obj: any = {};
    message.accessToken !== undefined && (obj.accessToken = message.accessToken);
    message.idToken !== undefined && (obj.idToken = message.idToken);
    message.refreshToken !== undefined && (obj.refreshToken = message.refreshToken);
    message.tokenType !== undefined && (obj.tokenType = message.tokenType);
    message.expiresIn !== undefined && (obj.expiresIn = Math.round(message.expiresIn));
    message.firebaseToken !== undefined && (obj.firebaseToken = message.firebaseToken);
    return obj;
  },

  create<I extends Exact<DeepPartial<Token>, I>>(base?: I): Token {
    return Token.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Token>, I>>(object: I): Token {
    const message = createBaseToken();
    message.accessToken = object.accessToken ?? "";
    message.idToken = object.idToken ?? "";
    message.refreshToken = object.refreshToken ?? "";
    message.tokenType = object.tokenType ?? "";
    message.expiresIn = object.expiresIn ?? 0;
    message.firebaseToken = object.firebaseToken ?? "";
    return message;
  },
};

function createBaseUserRecord(): UserRecord {
  return {
    uid: "",
    friendlyId: "",
    email: "",
    emailVerified: false,
    displayName: "",
    photoUrl: "",
    phoneNumber: undefined,
    providerUserInfo: [],
    disabled: false,
    prohibited: false,
  };
}

export const UserRecord = {
  fromJSON(object: any): UserRecord {
    return {
      uid: isSet(object.uid) ? String(object.uid) : "",
      friendlyId: isSet(object.friendlyId) ? String(object.friendlyId) : "",
      email: isSet(object.email) ? String(object.email) : "",
      emailVerified: isSet(object.emailVerified) ? Boolean(object.emailVerified) : false,
      displayName: isSet(object.displayName) ? String(object.displayName) : "",
      photoUrl: isSet(object.photoUrl) ? String(object.photoUrl) : "",
      phoneNumber: isSet(object.phoneNumber) ? HashData.fromJSON(object.phoneNumber) : undefined,
      providerUserInfo: Array.isArray(object?.providerUserInfo)
        ? object.providerUserInfo.map((e: any) => ProviderUserInfo.fromJSON(e))
        : [],
      disabled: isSet(object.disabled) ? Boolean(object.disabled) : false,
      prohibited: isSet(object.prohibited) ? Boolean(object.prohibited) : false,
    };
  },

  toJSON(message: UserRecord): unknown {
    const obj: any = {};
    message.uid !== undefined && (obj.uid = message.uid);
    message.friendlyId !== undefined && (obj.friendlyId = message.friendlyId);
    message.email !== undefined && (obj.email = message.email);
    message.emailVerified !== undefined && (obj.emailVerified = message.emailVerified);
    message.displayName !== undefined && (obj.displayName = message.displayName);
    message.photoUrl !== undefined && (obj.photoUrl = message.photoUrl);
    message.phoneNumber !== undefined &&
      (obj.phoneNumber = message.phoneNumber ? HashData.toJSON(message.phoneNumber) : undefined);
    if (message.providerUserInfo) {
      obj.providerUserInfo = message.providerUserInfo.map((e) => e ? ProviderUserInfo.toJSON(e) : undefined);
    } else {
      obj.providerUserInfo = [];
    }
    message.disabled !== undefined && (obj.disabled = message.disabled);
    message.prohibited !== undefined && (obj.prohibited = message.prohibited);
    return obj;
  },

  create<I extends Exact<DeepPartial<UserRecord>, I>>(base?: I): UserRecord {
    return UserRecord.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<UserRecord>, I>>(object: I): UserRecord {
    const message = createBaseUserRecord();
    message.uid = object.uid ?? "";
    message.friendlyId = object.friendlyId ?? "";
    message.email = object.email ?? "";
    message.emailVerified = object.emailVerified ?? false;
    message.displayName = object.displayName ?? "";
    message.photoUrl = object.photoUrl ?? "";
    message.phoneNumber = (object.phoneNumber !== undefined && object.phoneNumber !== null)
      ? HashData.fromPartial(object.phoneNumber)
      : undefined;
    message.providerUserInfo = object.providerUserInfo?.map((e) => ProviderUserInfo.fromPartial(e)) || [];
    message.disabled = object.disabled ?? false;
    message.prohibited = object.prohibited ?? false;
    return message;
  },
};

function createBaseProviderUserInfo(): ProviderUserInfo {
  return { providerId: "", federatedId: "" };
}

export const ProviderUserInfo = {
  fromJSON(object: any): ProviderUserInfo {
    return {
      providerId: isSet(object.providerId) ? String(object.providerId) : "",
      federatedId: isSet(object.federatedId) ? String(object.federatedId) : "",
    };
  },

  toJSON(message: ProviderUserInfo): unknown {
    const obj: any = {};
    message.providerId !== undefined && (obj.providerId = message.providerId);
    message.federatedId !== undefined && (obj.federatedId = message.federatedId);
    return obj;
  },

  create<I extends Exact<DeepPartial<ProviderUserInfo>, I>>(base?: I): ProviderUserInfo {
    return ProviderUserInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<ProviderUserInfo>, I>>(object: I): ProviderUserInfo {
    const message = createBaseProviderUserInfo();
    message.providerId = object.providerId ?? "";
    message.federatedId = object.federatedId ?? "";
    return message;
  },
};

function createBaseHashData(): HashData {
  return { value: "", method: "" };
}

export const HashData = {
  fromJSON(object: any): HashData {
    return {
      value: isSet(object.value) ? String(object.value) : "",
      method: isSet(object.method) ? String(object.method) : "",
    };
  },

  toJSON(message: HashData): unknown {
    const obj: any = {};
    message.value !== undefined && (obj.value = message.value);
    message.method !== undefined && (obj.method = message.method);
    return obj;
  },

  create<I extends Exact<DeepPartial<HashData>, I>>(base?: I): HashData {
    return HashData.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<HashData>, I>>(object: I): HashData {
    const message = createBaseHashData();
    message.value = object.value ?? "";
    message.method = object.method ?? "";
    return message;
  },
};

function createBaseOAuthCredential(): OAuthCredential {
  return { facebook: undefined, facebookToken: undefined, apple: undefined, legacy: undefined };
}

export const OAuthCredential = {
  fromJSON(object: any): OAuthCredential {
    return {
      facebook: isSet(object.facebook) ? FacebookCredential.fromJSON(object.facebook) : undefined,
      facebookToken: isSet(object.facebookToken) ? String(object.facebookToken) : undefined,
      apple: isSet(object.apple) ? AppleCredential.fromJSON(object.apple) : undefined,
      legacy: isSet(object.legacy) ? LegacyCredential.fromJSON(object.legacy) : undefined,
    };
  },

  toJSON(message: OAuthCredential): unknown {
    const obj: any = {};
    message.facebook !== undefined &&
      (obj.facebook = message.facebook ? FacebookCredential.toJSON(message.facebook) : undefined);
    message.facebookToken !== undefined && (obj.facebookToken = message.facebookToken);
    message.apple !== undefined && (obj.apple = message.apple ? AppleCredential.toJSON(message.apple) : undefined);
    message.legacy !== undefined && (obj.legacy = message.legacy ? LegacyCredential.toJSON(message.legacy) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<OAuthCredential>, I>>(base?: I): OAuthCredential {
    return OAuthCredential.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<OAuthCredential>, I>>(object: I): OAuthCredential {
    const message = createBaseOAuthCredential();
    message.facebook = (object.facebook !== undefined && object.facebook !== null)
      ? FacebookCredential.fromPartial(object.facebook)
      : undefined;
    message.facebookToken = object.facebookToken ?? undefined;
    message.apple = (object.apple !== undefined && object.apple !== null)
      ? AppleCredential.fromPartial(object.apple)
      : undefined;
    message.legacy = (object.legacy !== undefined && object.legacy !== null)
      ? LegacyCredential.fromPartial(object.legacy)
      : undefined;
    return message;
  },
};

function createBaseVerifyCodeCredential(): VerifyCodeCredential {
  return { phone: undefined, phoneVoice: undefined, resendLengthen: false };
}

export const VerifyCodeCredential = {
  fromJSON(object: any): VerifyCodeCredential {
    return {
      phone: isSet(object.phone) ? String(object.phone) : undefined,
      phoneVoice: isSet(object.phoneVoice) ? String(object.phoneVoice) : undefined,
      resendLengthen: isSet(object.resendLengthen) ? Boolean(object.resendLengthen) : false,
    };
  },

  toJSON(message: VerifyCodeCredential): unknown {
    const obj: any = {};
    message.phone !== undefined && (obj.phone = message.phone);
    message.phoneVoice !== undefined && (obj.phoneVoice = message.phoneVoice);
    message.resendLengthen !== undefined && (obj.resendLengthen = message.resendLengthen);
    return obj;
  },

  create<I extends Exact<DeepPartial<VerifyCodeCredential>, I>>(base?: I): VerifyCodeCredential {
    return VerifyCodeCredential.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<VerifyCodeCredential>, I>>(object: I): VerifyCodeCredential {
    const message = createBaseVerifyCodeCredential();
    message.phone = object.phone ?? undefined;
    message.phoneVoice = object.phoneVoice ?? undefined;
    message.resendLengthen = object.resendLengthen ?? false;
    return message;
  },
};

function createBaseFacebookCredential(): FacebookCredential {
  return { code: "", redirectUrl: "", nonce: "" };
}

export const FacebookCredential = {
  fromJSON(object: any): FacebookCredential {
    return {
      code: isSet(object.code) ? String(object.code) : "",
      redirectUrl: isSet(object.redirectUrl) ? String(object.redirectUrl) : "",
      nonce: isSet(object.nonce) ? String(object.nonce) : "",
    };
  },

  toJSON(message: FacebookCredential): unknown {
    const obj: any = {};
    message.code !== undefined && (obj.code = message.code);
    message.redirectUrl !== undefined && (obj.redirectUrl = message.redirectUrl);
    message.nonce !== undefined && (obj.nonce = message.nonce);
    return obj;
  },

  create<I extends Exact<DeepPartial<FacebookCredential>, I>>(base?: I): FacebookCredential {
    return FacebookCredential.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<FacebookCredential>, I>>(object: I): FacebookCredential {
    const message = createBaseFacebookCredential();
    message.code = object.code ?? "";
    message.redirectUrl = object.redirectUrl ?? "";
    message.nonce = object.nonce ?? "";
    return message;
  },
};

function createBaseAppleCredential(): AppleCredential {
  return { code: "", nonce: "" };
}

export const AppleCredential = {
  fromJSON(object: any): AppleCredential {
    return {
      code: isSet(object.code) ? String(object.code) : "",
      nonce: isSet(object.nonce) ? String(object.nonce) : "",
    };
  },

  toJSON(message: AppleCredential): unknown {
    const obj: any = {};
    message.code !== undefined && (obj.code = message.code);
    message.nonce !== undefined && (obj.nonce = message.nonce);
    return obj;
  },

  create<I extends Exact<DeepPartial<AppleCredential>, I>>(base?: I): AppleCredential {
    return AppleCredential.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<AppleCredential>, I>>(object: I): AppleCredential {
    const message = createBaseAppleCredential();
    message.code = object.code ?? "";
    message.nonce = object.nonce ?? "";
    return message;
  },
};

function createBaseLegacyCredential(): LegacyCredential {
  return { deviceId: "", token: "" };
}

export const LegacyCredential = {
  fromJSON(object: any): LegacyCredential {
    return {
      deviceId: isSet(object.deviceId) ? String(object.deviceId) : "",
      token: isSet(object.token) ? String(object.token) : "",
    };
  },

  toJSON(message: LegacyCredential): unknown {
    const obj: any = {};
    message.deviceId !== undefined && (obj.deviceId = message.deviceId);
    message.token !== undefined && (obj.token = message.token);
    return obj;
  },

  create<I extends Exact<DeepPartial<LegacyCredential>, I>>(base?: I): LegacyCredential {
    return LegacyCredential.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<LegacyCredential>, I>>(object: I): LegacyCredential {
    const message = createBaseLegacyCredential();
    message.deviceId = object.deviceId ?? "";
    message.token = object.token ?? "";
    return message;
  },
};

function createBasePushSettings(): PushSettings {
  return { notifyPromotion: false, notifyLogistics: false };
}

export const PushSettings = {
  fromJSON(object: any): PushSettings {
    return {
      notifyPromotion: isSet(object.notifyPromotion) ? Boolean(object.notifyPromotion) : false,
      notifyLogistics: isSet(object.notifyLogistics) ? Boolean(object.notifyLogistics) : false,
    };
  },

  toJSON(message: PushSettings): unknown {
    const obj: any = {};
    message.notifyPromotion !== undefined && (obj.notifyPromotion = message.notifyPromotion);
    message.notifyLogistics !== undefined && (obj.notifyLogistics = message.notifyLogistics);
    return obj;
  },

  create<I extends Exact<DeepPartial<PushSettings>, I>>(base?: I): PushSettings {
    return PushSettings.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<PushSettings>, I>>(object: I): PushSettings {
    const message = createBasePushSettings();
    message.notifyPromotion = object.notifyPromotion ?? false;
    message.notifyLogistics = object.notifyLogistics ?? false;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
